<script setup lang="ts">
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { useCoreStore } from '@apps/main/new-desktop/core/stores';

import {
  AppFlicker,
  AppMasonryGallery,
  AppStars,
  GalleryButtonType,
} from '@libs/ui';

import type { PeekPriceModalHotelOnlyItinerary } from '../models';

import PeekPriceHotelRooms from './PeekPriceHotelRooms.vue';

interface PeekPriceLargeHotelCardProps {
  itinerary: PeekPriceModalHotelOnlyItinerary;
}

const props = defineProps<PeekPriceLargeHotelCardProps>();

const coreStore = useCoreStore();
</script>

<template>
  <div class="peek-price-large-hotel-card">
    <AppMasonryGallery
      :image-urls="props.itinerary.hotel.imageUrls"
      :alt-image-text="$t('a11y.image')"
      :gallery-button="{
        type: GalleryButtonType.NoButton,
      }"
      orientation="horizontal"
      @img-load-error="
        (src) =>
          coreStore.handleImageLoadError({
            src: src,
            source: 'peek-price-large-hotel-card-masonry-gallery',
          })
      "
    />
    <div class="stay-details">
      <div class="time-section">
        <div class="date">
          <span v-if="props.itinerary.hotel.dates" class="date-text">
            {{ props.itinerary.hotel.dates }}
          </span>
          <div v-else class="date-flicker">
            <AppFlicker height="100%" width="spacing-96" />
          </div>
        </div>
        <div class="nights-text">{{ props.itinerary.hotel.nights }}</div>
      </div>
      <div class="hotel-name-text">{{ props.itinerary.hotel.hotelName }}</div>
      <div class="hotel-destination-text">
        {{ props.itinerary.hotel.hotelDestination }}
      </div>

      <div class="rating-section">
        <AppStars :amount="props.itinerary.hotel.hotelStars" size="18" />
        <CustomersRatingTag
          v-if="props.itinerary.hotel.guestRating"
          :guest-rating="props.itinerary.hotel.guestRating"
          font-size="s"
        />
      </div>

      <div class="rooms-section">
        <PeekPriceHotelRooms
          v-if="props.itinerary.hotel.rooms"
          :rooms="props.itinerary.hotel.rooms"
        />
        <AppFlicker v-else height="spacing-16" width="spacing-96" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.peek-price-large-hotel-card {
  min-height: 363px;
  height: 100%;
  display: grid;
  grid-template-rows: 55% 45%;

  .stay-details {
    .time-section {
      display: flex;
      margin: var(--spacing-12) 0;

      .date {
        padding-right: var(--spacing-8);
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);

        .date-text {
          @include font-label-m-light;
          color: var(--color-neutral-70);
        }

        .date-flicker {
          --date-text-height: 18.5px;
          height: var(--date-text-height);
        }
      }

      .nights-text {
        @include font-label-m-light;
        color: var(--color-neutral-70);
      }
    }

    .hotel-name-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
      margin-bottom: var(--spacing-4);
    }

    .hotel-destination-text {
      @include font-label-s-light;
      color: var(--color-neutral-70);
      margin-bottom: var(--spacing-8);
    }

    .rating-section {
      display: flex;
      align-items: center;
      gap: var(--spacing-12);
    }

    .rooms-section {
      margin-top: var(--spacing-16);
    }
  }
}
</style>
